import type { CustomCategory, CustomCategoryCustomFields } from '../types/customCategory';
import { getTranslatedProperty } from '@shopware-pwa/helpers-next';
import type { Supplier } from '../types/customManufacturer';
import { MediaIncludes, MinimalCategoryIncludes } from '../utils/includes';

export function useSupplier() {
    const { apiClient } = useShopwareContext();

    const getSupplierById = async (supplierId: string) => {
        return useAsyncData(`supplier-${supplierId}`, async () => {
            try {
                return (
                    await apiClient.invoke('getSupplier get /supplier/{id}', {
                        pathParams: {
                            id: supplierId,
                        },
                    })
                ).data;
            } catch (error) {
                return null;
            }
        });
    };

    const getSupplier = async (category: Ref<CustomCategory> | undefined) => {
        if (!category) {
            return null;
        }

        const customFields = getTranslatedProperty(
            category.value,
            'customFields',
        ) as unkown as CustomCategoryCustomFields;
        const supplierId = customFields.supplier_id;

        if (!supplierId) {
            return null;
        }

        const { data: supplier } = await getSupplierById(supplierId);
        return supplier.value as Supplier | null;
    };

    const getSocialMediaLinks = (supplier: Supplier | null) => {
        if (!supplier) {
            return null;
        }

        const socialMediaLinks = [
            {
                name: 'LinkedIn',
                url: supplier.translated.customFields?.linkedin,
            },
            {
                name: 'Facebook',
                url: supplier.translated.customFields?.facebook,
            },
            {
                name: 'Instagram',
                url: supplier.translated.customFields?.instagram,
            },
            {
                name: 'YouTube',
                url: supplier.translated.customFields?.youtube,
            },
        ];

        return socialMediaLinks.filter(
            (socialMediaLink: { name: string; url: string | undefined }) => socialMediaLink.url !== undefined,
        );
    };

    const getProductCategories = async (category: CustomCategory | undefined) => {
        // the category structure is the following:
        // supplier/products/category1 or with an example: meisinger/products/dental
        // we need the url for the products category and all sub categories

        if (!category) {
            return null;
        }

        const { data } = await useAsyncData(`category-${category.id}`, async () => {
            try {
                return (
                    await apiClient.invoke('getCategory post /category', {
                        body: {
                            filter: [
                                {
                                    field: 'parentId',
                                    type: 'equals',
                                    value: category.id,
                                },
                            ],
                            associations: {
                                children: {
                                    associations: {
                                        media: {},
                                        seoUrls: {},
                                    },
                                },
                                seoUrls: {},
                                media: {},
                            },
                            includes: {
                                media: MediaIncludes,
                                category: [...MinimalCategoryIncludes, ...['children', 'media', 'mediaId']],
                            },
                            limit: 1,
                        },
                    })
                ).data;
            } catch (error) {
                return null;
            }
        });

        return data.value?.elements?.at(0);
    };

    return {
        getSupplierById,
        getSupplier,
        getSocialMediaLinks,
        getProductCategories,
    };
}
